import {NonReducibleUnknown} from 'xstate';
import {GuardPredicate} from 'xstate/guards';
import {isSimpleSeriesSingleAgeRestricted} from '../../../../common/hook/utils';
import {requestContext} from '../../../../common/requestContext';
import {ApiStatusCodeVo} from '../../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {getSerializedPagewebError} from '../../../../errors/getSerializedPagewebError.function';
import {PagewebErrorType} from '../../../../errors/PagewebErrorType.enum';
import {
  AgeGrade,
  QueryFromPage,
  ReadAccessType,
  SaleMethod,
  SaleState,
  TicketType,
} from '../../../../graphql/generated/types';
import {checkWebAccessible} from '../../../content/[seriesId]/viewer/[productId]/util/checkWebAccessible';
import {
  BuyTicketFlowContext,
  BuyTicketFlowEvents,
  BuyTicketFlowGuards,
} from '../type';

export const buyTicketFlowGuards: {
  [x in keyof BuyTicketFlowGuards]: GuardPredicate<
    BuyTicketFlowContext,
    BuyTicketFlowEvents,
    NonReducibleUnknown,
    {type: string; params: NonReducibleUnknown}
  >;
} = {
  'if 뷰어 외부': ({context}) =>
    context.ticketProcessParam?.queryFrom !== QueryFromPage.ViewerEntry,
  'if 뷰어 내부': ({context}) =>
    context.ticketProcessParam?.queryFrom === QueryFromPage.ViewerEntry,
  'if isAnonymous': ({context}) => !context.user,
  'if 로그인 & 이용권 구매 플로우': ({context}) =>
    context.flowType === 'TICKET_PROCESS' && !!context.user,
  'if 로그인 & 세트 구매 플로우': ({context}) =>
    context.flowType === 'BUY_SET_PROCESS' && !!context.user,
  'if 웹 접근 불가': ({context}) => {
    return !checkWebAccessible({
      isMobile: !!requestContext?.requestInfo?.display?.isMobile,
      slideType: context?.simpleSeriesSingleInfo?.singleSlideType,
      isTextViewer: context.simpleSeriesSingleInfo?.singleIsTextViewer,
    });
  },
  'if 연령제한': ({context}) =>
    isSimpleSeriesSingleAgeRestricted(
      context.simpleSeriesSingleInfo,
      context.user
    ),
  'if 무료 & 19세 미만 작품 & 판매중': ({context}) =>
    !!(
      context.simpleSeriesSingleInfo?.singleIsFree &&
      (!context.simpleSeriesSingleInfo?.singleAgeGrade ||
        context.simpleSeriesSingleInfo.singleAgeGrade < AgeGrade.Nineteen) &&
      context.simpleSeriesSingleInfo.seriesSaleState === SaleState.Ing &&
      context.simpleSeriesSingleInfo?.singleSaleState === SaleState.Ing
    ),
  'if 열람 권한 유효 (RAT2)': ({context}) =>
    !!context.readyToUseTicket?.single?.readAccessType &&
    context.readyToUseTicket.single.readAccessType === ReadAccessType.CanRead,
  'if 뷰어 외부 & 무료회차 (RAT4)': ({context}) =>
    context.ticketProcessParam?.queryFrom !== QueryFromPage.ViewerEntry &&
    !!context.readyToUseTicket?.single?.readAccessType &&
    context.readyToUseTicket.single.readAccessType ===
      ReadAccessType.CanReadSerialize,
  'if 뷰어 내부 & 무료회차 (RAT4)': ({context}) =>
    context.ticketProcessParam?.queryFrom === QueryFromPage.ViewerEntry &&
    !!context.readyToUseTicket?.single?.readAccessType &&
    context.readyToUseTicket.single.readAccessType ===
      ReadAccessType.CanReadSerialize,
  'if 만료(RAT3) & 회차 끝까지 읽었거나 회차 선택 완료': ({context}) =>
    !!(
      context.readyToUseTicket?.single?.readAccessType ===
        ReadAccessType.Expired &&
      (context.readyToUseTicket?.single?.isDone ||
        context.ticketProcessParam?.pickExactly)
    ),
  'if 만료(RAT3) & 만료컨펌(expiredConfirmed) 체크 미완료': ({context}) =>
    context.readyToUseTicket?.single?.readAccessType ===
      ReadAccessType.Expired && !context.expiredConfirmed,
  'if hasError': ({context}) => !!context.error,
  'if 이용권_확인_팝업_OFF': ({context}) =>
    !context.ticketProcessParam?.popupOn,
  'if 이용권_확인_팝업_ON': ({context}) =>
    !!context.ticketProcessParam?.popupOn,
  'if 소장권만 보유': ({context}) => {
    const available = context.readyToUseTicket?.available;
    return !!(
      available &&
      available.ticketOwnType &&
      !available.ticketRentalType
    );
  },
  'if 대여권만 보유': ({context}) => {
    const available = context.readyToUseTicket?.available;
    return !!(
      available &&
      available.ticketRentalType &&
      !available.ticketOwnType
    );
  },
  'if 대여권 및 소장권 미보유': ({context}) => {
    const available = context.readyToUseTicket?.available;
    return !(
      available &&
      available.ticketOwnType &&
      available.ticketRentalType
    );
  },
  'if 이용권타입인데 대여권, 소장권 둘다 미보유': ({context}) => {
    if (context.readyToUseTicket?.series?.saleMethod === SaleMethod.Cash) {
      return false;
    }
    const available = context.readyToUseTicket?.available;
    return (
      !available || (!available.ticketOwnType && !available.ticketRentalType)
    );
  },
  'if 보던화 선택': ({event}) => event.output === 'CONTINUE',
  'if 다음화 선택': ({event}) => event.output === 'NEXT',
  'if 정주행 & 대여권 보유': ({context}) =>
    !!context.ticketProcessParam?.nonstopWatching &&
    !!context.readyToUseTicket?.available?.ticketRentalType,
  'if 정주행 & 소장권 보유': ({context}) =>
    !!context.ticketProcessParam?.nonstopWatching &&
    !!context.readyToUseTicket?.available?.ticketOwnType,
  'if 정주행 & 기본 이용권 선택': ({context}) =>
    !!context.ticketProcessParam?.nonstopWatching &&
    !!context.selectedNonstopWatchingBuyTicketInfo,
  'if 정주행 & 기본 이용권 미선택': ({context}) =>
    !!context.ticketProcessParam?.nonstopWatching &&
    !context.selectedNonstopWatchingBuyTicketInfo,
  'if 세트의_모든_회차_소장중_에러 발생': ({event}) => {
    const serializedError = getSerializedPagewebError(event.error);
    return (
      serializedError.type === PagewebErrorType.NicolasApiStatusError &&
      serializedError.resultCode === ApiStatusCodeVo.SetAllOwned
    );
  },
  'if 이미_구매한_세트상품_에러 발생': ({event}) => {
    const serializedError = getSerializedPagewebError(event.error);
    return (
      serializedError.type === PagewebErrorType.NicolasApiStatusError &&
      serializedError.resultCode === ApiStatusCodeVo.SetAlreadyPurchased
    );
  },
  'if 구매완료팝업_작품홈가기 노출': ({event}) =>
    event.output === 'GO_TO_CONTENTHOME',
  'if 구매완료팝업_뷰어가기_노출': ({event}) => event.output === 'GO_TO_VIEWER',
  'if isCashTypeReadyToUse': ({context}) =>
    context.readyToUseTicket?.series?.saleMethod === SaleMethod.Cash,
  'if 보유한 세트 이용권 존재': ({context}) =>
    !!context.readyToUseTicket?.available &&
    (context.readyToUseTicket?.available.ticketRentalType ===
      TicketType.RentSet ||
      context.readyToUseTicket?.available.ticketOwnType === TicketType.BuySet),
  'if 로그인 팝업 미노출': ({context}) => !context.loginPopupConfirm,
  'if 이용권_구매_캐시_부족_에러 발생': ({event}) => {
    const serializedError = getSerializedPagewebError(event.error);
    return (
      serializedError.type === PagewebErrorType.NicolasApiStatusError &&
      serializedError.resultCode === ApiStatusCodeVo.CashNotEnough
    );
  },
  'if 로그인 & 이용권 구매 플로우 & 뷰어 내부 & 이용권 구매 후 바로보기': ({
    context,
  }) =>
    context.flowType === 'TICKET_PROCESS' &&
    !!context.user &&
    context.ticketProcessParam?.queryFrom === QueryFromPage.ViewerEntry &&
    !!context.ticketProcessParam.ticketTypeToUseQuery,
  'if 저장된 이용권 정보 존재': ({context}) => !!context.storedTicketInfo,
  'if 에러발생': ({event}) =>
    !!event.error &&
    (event.error instanceof Error || typeof event.error === 'string'),
};
